import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { useObserver } from 'mobx-react-lite';

import useUser from './use-user';

const GET_USER_SEGMENTS = gql`
  query GetUserSegments($dispensaryId: String!) {
    meConsumer {
      _id
      segmentIds(dispensaryId: $dispensaryId)
    }
  }
`;

export type UseUserSegmentsProps = {
  dispensaryId?: string;
};

export type UseUserSegmentsReturn = {
  segments: string[];
  loading: boolean;
};

/**
 * Hook to fetch the current user's segment IDs for a dispensary.
 *
 * Used by exclusive collections to include segments in GraphQL query variables,
 * enabling Apollo and CDN to cache separately for different segment combinations.
 *
 * Uses cache-first (segments don't change frequently) and refetches automatically
 * on login/logout via apolloClient.clearStore(). Anonymous users get empty array.
 */
export function useUserSegments({ dispensaryId }: UseUserSegmentsProps): UseUserSegmentsReturn {
  const User = useUser();
  const isLoggedIn = useObserver(() => User.isLoggedIn);

  const { data, loading } = useQuery(GET_USER_SEGMENTS, {
    variables: { dispensaryId: dispensaryId ?? '' },
    skip: !isLoggedIn || !dispensaryId,
    fetchPolicy: 'cache-first',
  });

  return {
    segments: data?.meConsumer?.segmentIds || [],
    loading: isLoggedIn && loading, // Only show loading for logged-in users
  };
}
